import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Check, Loader2, ChevronLeft, ChevronRight, Handshake, PenLine, FileCheck2, Trash2, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import api from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useLang, translateFr as tfr } from "@/i18n";
import { NegotiationDocs } from "./NegotiationDocs";
import { NegotiationReconciliation } from "./NegotiationReconciliation";
import { NegotiationBriefs } from "./NegotiationBriefs";
import { NegotiationAnalytics } from "./NegotiationAnalytics";
import { CounterOfferExport } from "./CounterOfferExport";
import { eventLabel } from "./negotiationEvents";
import { validityTone, remainingLabel, fmtDeadline, isoToLocalInput } from "./negotiationShared";
import { NegotiationDeliveries } from "./NegotiationDeliveries";

const NEG_STATUS_FR = {
  draft: tfr("Brouillon"), offer_received: tfr("Offre reçue"), under_review: "En analyse",
  counter_prepared: tfr("Contre-offre préparée"), counter_sent: tfr("Contre-offre envoyée"),
  awaiting_response: tfr("En attente de réponse"), revised_received: tfr("Nouvelle offre reçue"),
  negotiating: tfr("En négociation"), verbal_agreement: tfr("Accord verbal"), final_received: tfr("Contrat final reçu"),
  ready_to_sign: tfr("Prêt à signer"), signed: tfr("Signé"), rejected: tfr("Refusé"), withdrawn: tfr("Retiré"), closed: tfr("Clôturé"),
};
const TERM_STATUS_FR = {
  not_discussed: tfr("Non abordé"), open: tfr("Ouvert"), negotiating: tfr("En négociation"), accepted: tfr("Accepté"),
  partially_accepted: tfr("Partiellement accepté"), rejected: tfr("Refusé"), removed: tfr("Retiré"), needs_clarification: tfr("À clarifier"),
};
const VTYPE_FR = { offer: tfr("Offre initiale"), counter_offer: tfr("Contre-offre"), revised_offer: tfr("Offre révisée"), final_agreement: tfr("Accord final") };
const PARTY_FR = { counterparty: "Contrepartie", agent: "Agent", athlete: tfr("Athlète") };
const CHECK_FR = {
  financial: tfr("Conditions financières vérifiées"), duration: tfr("Durée vérifiée"), options: tfr("Options vérifiées"),
  commission: tfr("Commission vérifiée"), clauses: tfr("Clauses spécifiques vérifiées"),
  document: "Document final joint", differences: tfr("Différences vérifiées"),
};
const STATUS_TONE = {
  accepted: "text-emerald-500 bg-emerald-500/10 border-emerald-500/25",
  partially_accepted: "text-emerald-500 bg-emerald-500/10 border-emerald-500/25",
  negotiating: "text-amber-500 bg-amber-500/10 border-amber-500/25",
  open: "text-amber-500 bg-amber-500/10 border-amber-500/25",
  needs_clarification: "text-sky-500 bg-sky-500/10 border-sky-500/25",
  rejected: "text-red-500 bg-red-500/10 border-red-500/25",
  removed: "text-red-500 bg-red-500/10 border-red-500/25",
  not_discussed: "text-muted-foreground bg-secondary/50 border-border dark:border-border",
};

function useFmt() {
  const { locale, tf } = useLang();
  return useCallback((row, val) => {
    if (val === null || val === undefined || val === "") return "—";
    if (typeof val === "object") {
      if (val.amount !== undefined && val.amount !== null) {
        try { return new Intl.NumberFormat(locale, { style: "currency", currency: val.currency || row.currency || "EUR", maximumFractionDigits: 0 }).format(val.amount); }
        catch { return `${val.amount} ${val.currency || ""}`; }
      }
      return val.value_text ? tf(String(val.value_text)) : "—";
    }
    if (row.vtype === "money") {
      try { return new Intl.NumberFormat(locale, { style: "currency", currency: row.currency || "EUR", maximumFractionDigits: 0 }).format(val); }
      catch { return `${val} ${row.currency || ""}`; }
    }
    if (row.vtype === "percent") return `${val} %`;
    if (row.vtype === "bool" || typeof val === "boolean") return val ? tf("Oui") : tf("Non");
    return String(tf(String(val)));
  }, [locale, tf]);
}

const card = "rounded-2xl border border-border dark:border-border bg-card dark:bg-surface";

export function TabNegotiation({ contract, onChanged }) {
  const { tf, locale } = useLang();
  const fmt = useFmt();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [termIdx, setTermIdx] = useState(0);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [exportVersion, setExportVersion] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/contracts/${contract.id}/negotiation`);
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract.id]);
  useEffect(() => { load(); }, [load]);

  const neg = data?.negotiation;
  const versions = data?.versions || [];
  const finals = versions.filter((v) => v.status === "final");
  const draft = versions.find((v) => v.status === "draft");
  const comparison = data?.comparison || [];
  const summary = data?.summary;

  const act = async (fn, ok) => {
    if (busy) return;
    setBusy(true);
    try { await fn(); if (ok) toast.success(tf(ok)); await load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;

  if (!neg) {
    return (
      <div className={cn(card, "p-10 text-center")} data-testid="neg-empty">
        <Handshake className="h-8 w-8 mx-auto text-muted-foreground/60" />
        <p className="mt-3 font-medium">{tf("Aucune négociation active pour ce contrat.")}</p>
        <p className="text-sm text-muted-foreground mt-1">{tf("Ouvrez une négociation pour suivre offres, contre-offres et points d'accord.")}</p>
        <Button className="mt-5" disabled={busy} data-testid="neg-start-btn"
          onClick={() => act(() => api.post(`/contracts/${contract.id}/negotiation`, {}), tfr("Négociation ouverte"))}>
          <Plus className="h-4 w-4 mr-1.5" /> {tf("Démarrer la négociation")}
        </Button>
      </div>
    );
  }

  const openPts = comparison.filter((r) => ["open", "negotiating", "partially_accepted", "needs_clarification"].includes(r.status));
  const agreedPts = comparison.filter((r) => r.status === "accepted");
  const rejectedPts = comparison.filter((r) => ["rejected", "removed"].includes(r.status));
  const reconBlocked = !!(data.reconciliation && data.reconciliation.overall !== "reconciled");
  const canSign = ["ready_to_sign", "verbal_agreement", "final_received"].includes(neg.status) && !reconBlocked;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4" data-testid="neg-workspace">
      <div className="space-y-4 min-w-0">
        {/* Status + actions */}
        <div className={cn(card, "p-4 flex flex-wrap items-center gap-2")} data-testid="neg-header">
          <Select value={neg.status} onValueChange={(v) => act(() => api.patch(`/negotiations/${neg.id}`, { status: v }))}>
            <SelectTrigger className="w-auto h-9 gap-1.5" data-testid="neg-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{(data.statuses || []).map((s) => <SelectItem key={s} value={s}>{tf(NEG_STATUS_FR[s] || s)}</SelectItem>)}</SelectContent>
          </Select>
          <div className="flex-1" />
          {!draft && (<>
            <Button variant="outline" size="sm" className="h-9" disabled={busy} data-testid="neg-record-offer-btn"
              onClick={() => act(() => api.post(`/negotiations/${neg.id}/versions`, { version_type: finals.length ? "revised_offer" : "offer", proposed_by: "counterparty" }))}>
              {tf(finals.length ? "Enregistrer une offre révisée" : "Enregistrer l'offre reçue")}
            </Button>
            {finals.length > 0 && (
              <Button size="sm" className="h-9" disabled={busy} data-testid="neg-counter-btn"
                onClick={() => act(() => api.post(`/negotiations/${neg.id}/versions`, { version_type: "counter_offer", proposed_by: "agent" }), tfr("Brouillon de contre-offre créé à partir de la dernière offre"))}>
                <PenLine className="h-3.5 w-3.5 mr-1.5" /> {tf("Créer une contre-offre")}
              </Button>
            )}
          </>)}
        </div>

        {/* Version stepper */}
        {finals.length > 0 && (
          <div className="flex gap-2 overflow-x-auto tabstrip-x pb-1" data-testid="neg-version-stepper">
            {finals.map((v) => (
              <div key={v.id} className={cn(card, "px-3 py-2 min-w-[150px]", neg.current_version_id === v.id && "border-red-500/50")}>
                <div className="text-xs font-semibold flex items-center gap-1.5">
                  <span>V{v.version_number} · {tf(VTYPE_FR[v.version_type])}</span>
                  {["agent", "athlete"].includes(v.proposed_by) && (
                    <button type="button" onClick={() => setExportVersion(v)} className="text-muted-foreground hover:text-red-500"
                      aria-label={tf("Exporter la contre-offre")} title={tf("Exporter la contre-offre")} data-testid={`neg-export-v${v.version_number}`}>
                      <FileDown className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                <div className="text-[11px] text-muted-foreground">{new Date(v.finalized_at || v.created_at).toLocaleDateString(locale)} · {tf(PARTY_FR[v.proposed_by])}</div>
                {v.validity_state && !["no_deadline", "accepted", "rejected", "withdrawn", "closed"].includes(v.validity_state) && (
                  <div className="mt-1.5" data-testid={`neg-validity-v${v.version_number}`}>
                    <span className={cn("px-1.5 py-0.5 rounded-md border text-[10px] font-semibold", validityTone(v.validity_state))}>
                      {remainingLabel(v.valid_until, v.validity_state, tf)}
                    </span>
                    {v.valid_until && v.validity_state !== "superseded" && (
                      <div className="text-[10px] text-muted-foreground mt-0.5">{fmtDeadline(v.valid_until, locale)}</div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Expired offer contextual actions */}
        {(() => {
          const latest = finals[finals.length - 1];
          const active = !["signed", "closed", "rejected", "withdrawn"].includes(neg.status);
          if (!latest || latest.validity_state !== "expired" || !active) return null;
          return (
            <div className="rounded-2xl border border-red-500/30 bg-red-500/5 p-3 flex flex-wrap items-center gap-2" data-testid="neg-expired-banner">
              <p className="text-xs font-medium flex-1 min-w-[200px]">{tf("Offre expirée — la contre-offre V")}{latest.version_number} {tf("est arrivée à échéance.")}</p>
              {!draft && (
                <Button size="sm" className="h-8" disabled={busy} data-testid="neg-expired-new-version-btn"
                  onClick={() => act(() => api.post(`/negotiations/${neg.id}/versions`, { version_type: "counter_offer", proposed_by: "agent" }), tfr("Brouillon de contre-offre créé à partir de la dernière offre"))}>
                  {tf("Créer une nouvelle version")}
                </Button>
              )}
            </div>
          );
        })()}

        {/* Draft editor — key remounts it when the server-side draft terms change (e.g. validated extraction) */}
        {draft && <DraftEditor
          key={`${draft.id}:${JSON.stringify((draft.terms || []).map((t) => [t.term_key, t.amount, t.currency, t.value_text, t.value_bool, !!t.source]))}`}
          neg={neg} draft={draft} catalog={data.catalog} onDone={() => { load(); onChanged?.(); }} />}

        {/* Documents per version + AI extraction */}
        <NegotiationDocs neg={neg} versions={versions} documents={data.documents || []}
          extractions={data.extractions || []} comparison={comparison} onChanged={() => { load(); onChanged?.(); }} />

        {/* Final reconciliation */}
        <NegotiationReconciliation neg={neg} documents={data.documents || []} extractions={data.extractions || []}
          reconciliation={data.reconciliation} onChanged={() => { load(); onChanged?.(); }} />

        {/* Deterministic analytics */}
        {finals.length > 0 && <NegotiationAnalytics key={finals.length} neg={neg} />}

        {/* Email delivery history */}
        <NegotiationDeliveries neg={neg} deliveries={data.deliveries || []} />

        {/* Comparison */}
        <div className={cn(card, "p-4")} data-testid="neg-comparison">
          <h3 className="text-[11px] uppercase tracking-[0.16em] font-semibold text-foreground/85 mb-3">{tf("Comparaison des offres")}</h3>
          {comparison.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="neg-comparison-empty">{tf("Ajoutez une offre ou un document de contrat pour commencer.")}</p>
          ) : (<>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-[11px] uppercase tracking-wider text-muted-foreground/80 text-left">
                  <th className="py-2 pr-3">{tf("Élément")}</th><th className="py-2 pr-3">{tf("Offre initiale")}</th>
                  <th className="py-2 pr-3">{tf("Notre contre-offre")}</th><th className="py-2 pr-3">{tf("Dernière offre")}</th>
                  <th className="py-2 pr-3">{tf("Objectif")}</th><th className="py-2 pr-3">{tf("Écart")}</th><th className="py-2">{tf("Statut")}</th>
                </tr></thead>
                <tbody>
                  {comparison.map((r) => (
                    <tr key={r.term_key} id={`term-${r.term_key}`} className="border-t border-border/60 dark:border-border" data-testid={`neg-row-${r.term_key}`}>
                      <td className="py-2.5 pr-3 font-medium">{tf(r.label)}</td>
                      <td className="py-2.5 pr-3">{fmt(r, r.initial)}</td>
                      <td className="py-2.5 pr-3">{fmt(r, r.ours)}</td>
                      <td className="py-2.5 pr-3">{fmt(r, r.latest)}</td>
                      <td className="py-2.5 pr-3">{fmt(r, r.target)}</td>
                      <td className="py-2.5 pr-3"><Delta row={r} fmt={fmt} /></td>
                      <td className="py-2"><TermStatus neg={neg} row={r} act={act} termStatuses={data.term_statuses} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobile per-term card */}
            <div className="md:hidden" data-testid="neg-mobile-term">
              {(() => { const r = comparison[Math.min(termIdx, comparison.length - 1)]; return (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Button variant="outline" size="sm" className="h-8 px-2" onClick={() => setTermIdx((i) => Math.max(0, i - 1))} disabled={termIdx === 0} data-testid="neg-term-prev"><ChevronLeft className="h-4 w-4" /></Button>
                    <div className="text-sm font-semibold">{tf(r.label)} <span className="text-muted-foreground font-normal">({termIdx + 1}/{comparison.length})</span></div>
                    <Button variant="outline" size="sm" className="h-8 px-2" onClick={() => setTermIdx((i) => Math.min(comparison.length - 1, i + 1))} disabled={termIdx >= comparison.length - 1} data-testid="neg-term-next"><ChevronRight className="h-4 w-4" /></Button>
                  </div>
                  <div className="space-y-1.5 text-sm">
                    {[[tfr("Offre initiale"), r.initial], [tfr("Notre contre-offre"), r.ours], [tfr("Dernière offre"), r.latest], ["Objectif", r.target]].map(([l, v]) => (
                      <div key={l} className="flex justify-between gap-3 py-1 border-b border-border/50 dark:border-border">
                        <span className="text-muted-foreground">{tf(l)}</span><span className="font-medium">{fmt(r, v)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between gap-3 py-1"><span className="text-muted-foreground">{tf("Écart")}</span><Delta row={r} fmt={fmt} /></div>
                    <div className="pt-1"><TermStatus neg={neg} row={r} act={act} termStatuses={data.term_statuses} /></div>
                  </div>
                </div>
              ); })()}
            </div>
          </>)}
        </div>

        {/* Open / agreed / rejected */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <PointsCard title={tfr("Points ouverts")} rows={openPts} tone="amber" fmt={fmt} onPick={(k) => { setTermIdx(comparison.findIndex((r) => r.term_key === k)); document.getElementById(`term-${k}`)?.scrollIntoView({ behavior: "smooth", block: "center" }); }} testid="neg-open-points" emptyText={tfr("Tous les points de négociation sont actuellement résolus.")} />
          <PointsCard title={tfr("Points acquis")} rows={agreedPts} tone="emerald" fmt={fmt} testid="neg-agreed-points" emptyText={tfr("Aucun point acquis pour le moment.")} />
          <PointsCard title={tfr("Points refusés")} rows={rejectedPts} tone="red" fmt={fmt} testid="neg-rejected-points" emptyText={tfr("Aucun point refusé.")} />
        </div>

        {/* Timeline */}
        <div className={cn(card, "p-4")} data-testid="neg-timeline">
          <h3 className="text-[11px] uppercase tracking-[0.16em] font-semibold text-foreground/85 mb-3">{tf("Historique des échanges")}</h3>
          {(data.timeline || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">{tf("Aucune activité de négociation pour le moment.")}</p>
          ) : (
            <ul className="space-y-2.5">
              {[...data.timeline].reverse().map((e) => (
                <li key={e.id} className="flex gap-3 text-sm">
                  <span className="mt-1.5 h-2 w-2 rounded-full bg-red-500/70 shrink-0" />
                  <div><div className="font-medium">{eventLabel(e, tf)}</div>
                    <div className="text-[11px] text-muted-foreground">{new Date(e.created_at).toLocaleString(locale)} · {e.actor}</div></div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* ==== SIDEBAR: Synthèse ==== */}
      <div className="space-y-4 min-w-0">
        <div className={cn(card, "p-4")} data-testid="neg-summary">
          <h3 className="text-[11px] uppercase tracking-[0.16em] font-semibold text-foreground/85 mb-3">{tf("Synthèse contractuelle")}</h3>
          <dl className="space-y-1.5 text-sm">
            {[[tfr("Athlète"), summary?.athlete], ["Contrepartie", summary?.counterparty], ["Sport", summary?.sport ? tf(summary.sport[0].toUpperCase() + summary.sport.slice(1)) : null],
              [tfr("Statut"), tf(NEG_STATUS_FR[neg.status])], [tfr("Version actuelle"), summary?.current_version ? `V${summary.current_version}` : null],
              [tfr("Durée"), summary?.duration ? `${summary.duration} ${tf("ans")}` : null],
              [tfr("Début"), summary?.start_date], ["Fin", summary?.end_date],
              ["Proposition actuelle", summary?.current_proposal ? fmt({ vtype: "money", currency: summary.current_proposal.currency }, summary.current_proposal.amount) : null],
              ["Notre position", summary?.our_position ? fmt({ vtype: "money", currency: summary.our_position.currency }, summary.our_position.amount) : null],
              ["Agent responsable", summary?.owner],
            ].map(([l, v]) => (
              <div key={l} className="flex justify-between gap-3"><dt className="text-muted-foreground">{tf(l)}</dt><dd className="font-medium text-right">{v || "—"}</dd></div>
            ))}
          </dl>
          <div className="flex flex-wrap gap-1.5 mt-3" data-testid="neg-counts">
            {[["accepted", tfr("acceptés"), "emerald"], ["negotiating", tfr("à négocier"), "amber"], ["rejected", tfr("refusés"), "red"], ["not_discussed", tfr("non abordés"), "zinc"]].map(([k, l]) => (
              <span key={k} className={cn("text-[11px] px-2 py-0.5 rounded-full border", STATUS_TONE[k === "not_discussed" ? "not_discussed" : k === "negotiating" ? "negotiating" : k])}>
                {(summary?.counts?.[k] ?? 0)} {tf(l)}
              </span>
            ))}
          </div>
        </div>

        {/* Athlynx Intelligence briefs */}
        <NegotiationBriefs neg={neg} />

        {/* Next action */}
        <NextActionCard neg={neg} act={act} />

        {/* Finalization */}
        <div className={cn(card, "p-4 space-y-2")} data-testid="neg-finalize">
          <h3 className="text-[11px] uppercase tracking-[0.16em] font-semibold text-foreground/85">{tf("Finalisation")}</h3>
          <Button variant="outline" size="sm" className="w-full h-9 justify-start" disabled={busy || !!neg.verbal_agreement_at} data-testid="neg-verbal-btn"
            onClick={() => act(() => api.post(`/negotiations/${neg.id}/verbal-agreement`), tfr("Accord verbal enregistré"))}>
            <Handshake className="h-3.5 w-3.5 mr-2" /> {neg.verbal_agreement_at ? tf("Accord verbal enregistré") : tf("Marquer l'accord verbal")}
          </Button>
          <Button variant="outline" size="sm" className="w-full h-9 justify-start" onClick={() => setReviewOpen(true)} data-testid="neg-review-btn">
            <FileCheck2 className="h-3.5 w-3.5 mr-2" /> {tf("Revue finale")}
          </Button>
          <Button variant="outline" size="sm" className="w-full h-9 justify-start" disabled={busy || !canSign} data-testid="neg-signed-btn"
            onClick={() => act(() => api.post(`/negotiations/${neg.id}/mark-signed`), tfr("Contrat marqué signé"))}>
            <Check className="h-3.5 w-3.5 mr-2" /> {tf("Marquer signé")}
          </Button>
          <Button size="sm" className="w-full h-9 justify-start" disabled={busy || neg.status !== "signed"} data-testid="neg-activate-btn"
            onClick={() => act(() => api.post(`/negotiations/${neg.id}/activate`), tfr("Contrat actif créé à partir des conditions validées"))}>
            <Check className="h-3.5 w-3.5 mr-2" /> {tf("Convertir en contrat actif")}
          </Button>
          {reconBlocked && <p className="text-[11px] text-amber-500" data-testid="neg-sign-blocked">{tf("Vérifiez les différences contractuelles avant de continuer.")}</p>}
          {neg.activated_at && <p className="text-[11px] text-emerald-500">{tf("Contrat activé — le suivi de renouvellement utilise les dates finales.")}</p>}
          <p className="text-[10px] text-muted-foreground">{tf("Un accord verbal n'est pas un contrat signé.")}</p>
        </div>
      </div>

      <ReviewDialog open={reviewOpen} onOpenChange={setReviewOpen} neg={neg} checklist={data.review_checklist} act={act} />
      <CounterOfferExport key={exportVersion?.id || "none"} open={!!exportVersion} onOpenChange={(o) => !o && setExportVersion(null)}
        neg={neg} version={exportVersion} summary={summary} catalog={data.catalog} onSent={() => { load(); onChanged?.(); }} />
    </div>
  );
}

function Delta({ row, fmt }) {
  const { tf } = useLang();
  if (row.delta !== null && row.delta !== undefined)
    return <span className={cn("font-medium", row.delta > 0 ? "text-emerald-500" : row.delta < 0 ? "text-red-500" : "text-muted-foreground")} data-testid={`neg-delta-${row.term_key}`}>
      {row.delta > 0 ? "+" : ""}{fmt(row, row.delta)}</span>;
  if (row.changed) return <span className="text-amber-500 text-xs font-medium">{tf("Modifiée")}</span>;
  return <span className="text-muted-foreground">—</span>;
}

function TermStatus({ neg, row, act, termStatuses }) {
  const { tf } = useLang();
  return (
    <Select value={row.status} onValueChange={(v) => act(() => api.patch(`/negotiations/${neg.id}`, { term_states: { [row.term_key]: v } }))}>
      <SelectTrigger className={cn("h-7 w-auto gap-1 text-[11px] rounded-full border px-2.5", STATUS_TONE[row.status])} data-testid={`neg-term-status-${row.term_key}`}>
        <SelectValue>{tf(TERM_STATUS_FR[row.status])}</SelectValue>
      </SelectTrigger>
      <SelectContent>{(termStatuses || []).map((s) => <SelectItem key={s} value={s}>{tf(TERM_STATUS_FR[s])}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function PointsCard({ title, rows, tone, fmt, onPick, testid, emptyText }) {
  const { tf } = useLang();
  return (
    <div className={cn(card, "p-4")} data-testid={testid}>
      <h4 className={cn("text-[11px] uppercase tracking-wider font-semibold mb-2", tone === "emerald" ? "text-emerald-500" : tone === "red" ? "text-red-500" : "text-amber-500")}>{tf(title)} ({rows.length})</h4>
      {rows.length === 0 ? <p className="text-xs text-muted-foreground">{tf(emptyText)}</p> : (
        <ul className="space-y-1.5">
          {rows.map((r) => (
            <li key={r.term_key}>
              <button type="button" onClick={() => onPick?.(r.term_key)} className="text-left w-full text-xs hover:underline">
                <span className="font-medium">{tf(r.label)}</span>
                <span className="text-muted-foreground"> · {fmt(r, r.latest ?? r.ours ?? r.initial)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function NextActionCard({ neg, act }) {
  const { tf, locale } = useLang();
  const [label, setLabel] = useState(neg.next_action?.label || "");
  const [due, setDue] = useState(neg.next_action?.due_date || "");
  return (
    <div className={cn(card, "p-4")} data-testid="neg-next-action">
      <h3 className="text-[11px] uppercase tracking-[0.16em] font-semibold text-foreground/85 mb-2">{tf("Prochaine action")}</h3>
      {neg.next_action?.label && (
        <p className="text-sm mb-2"><span className="font-medium">{neg.next_action.label}</span>
          {neg.next_action.due_date && <span className="text-muted-foreground"> · {new Date(neg.next_action.due_date).toLocaleDateString(locale)}</span>}</p>
      )}
      <div className="space-y-2">
        <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={tf("Ex. : Relancer le directeur sportif")} className="h-9" data-testid="neg-next-action-label" />
        <div className="flex gap-2">
          <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} className="h-9 min-w-0" data-testid="neg-next-action-due" />
          <Button size="sm" className="h-9" data-testid="neg-next-action-save"
            onClick={() => act(() => api.patch(`/negotiations/${neg.id}`, { next_action: label.trim() ? { label: label.trim(), due_date: due || null } : null }), tfr("Prochaine action enregistrée"))}>
            {tf("Enregistrer")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function DraftEditor({ neg, draft, catalog, onDone }) {
  const { tf } = useLang();
  const [terms, setTerms] = useState(draft.terms || []);
  const [addKey, setAddKey] = useState("");
  const [busy, setBusy] = useState(false);
  const byKey = useMemo(() => Object.fromEntries((catalog || []).map((c) => [c.term_key, c])), [catalog]);

  const setT = (i, patch) => setTerms((ts) => ts.map((t, j) => (j === i ? { ...t, ...patch } : t)));
  const addTerm = (k) => {
    const c = byKey[k];
    if (!c || terms.some((t) => t.term_key === k)) return;
    setTerms((ts) => [...ts, { term_key: k, vtype: c.vtype, category: c.category, amount: null, currency: c.vtype === "money" ? "EUR" : null, value_text: null, value_bool: false }]);
    setAddKey("");
  };
  const [validUntil, setValidUntil] = useState(isoToLocalInput(draft.valid_until));
  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(tf(ok)); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); }
    finally { setBusy(false); }
  };
  const save = () => api.patch(`/negotiations/${neg.id}/versions/${draft.id}`, {
    terms, valid_until: validUntil ? new Date(validUntil).toISOString() : null,
  });

  return (
    <div className={cn(card, "p-4 border-red-500/30")} data-testid="neg-draft-editor">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[11px] uppercase tracking-[0.16em] font-semibold text-foreground/85">
          {tf("Brouillon")} — V{draft.version_number} · {tf(VTYPE_FR[draft.version_type])} ({tf(PARTY_FR[draft.proposed_by])})
        </h3>
        <button type="button" onClick={() => run(() => api.delete(`/negotiations/${neg.id}/versions/${draft.id}`), tfr("Brouillon supprimé"))}
          className="text-muted-foreground hover:text-red-500" data-testid="neg-draft-delete" aria-label={tf("Supprimer")}><Trash2 className="h-4 w-4" /></button>
      </div>
      <div className="space-y-2">
        {terms.map((t, i) => {
          const c = byKey[t.term_key];
          const label = (c && tf(c.label)) || t.custom_label || t.term_key;
          return (
            <div key={t.term_key} className="flex items-center gap-2 flex-wrap" data-testid={`neg-draft-term-${t.term_key}`}>
              <span className="text-sm w-44 shrink-0 truncate">
                {label}
                {t.source && <span className="block text-[9px] text-emerald-500 truncate" data-testid={`neg-term-source-${t.term_key}`} title={`${t.source.document_name}${t.source.page ? ` · p.${t.source.page}` : ""}`}>{tf("Source")} : {t.source.document_name}{t.source.page ? ` · p.${t.source.page}` : ""}</span>}
              </span>
              {["money", "number", "percent"].includes(t.vtype) ? (<>
                <Input type="number" value={t.amount ?? ""} onChange={(e) => setT(i, { amount: e.target.value === "" ? null : Number(e.target.value) })} className="h-9 w-32 min-w-0" data-testid={`neg-draft-amount-${t.term_key}`} />
                {t.vtype === "money" && (
                  <Select value={t.currency || "EUR"} onValueChange={(v) => setT(i, { currency: v })}>
                    <SelectTrigger className="h-9 w-[76px]"><SelectValue /></SelectTrigger>
                    <SelectContent>{["EUR", "USD", "GBP", "CHF"].map((c2) => <SelectItem key={c2} value={c2}>{tfr(c2)}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </>) : t.vtype === "bool" ? (
                <label className="flex items-center gap-2 text-sm"><Checkbox checked={!!t.value_bool} onCheckedChange={(v) => setT(i, { value_bool: !!v })} /> {t.value_bool ? tf("Oui") : tf("Non")}</label>
              ) : (
                <Input value={t.value_text || ""} onChange={(e) => setT(i, { value_text: e.target.value })} className="h-9 flex-1 min-w-[140px]" data-testid={`neg-draft-text-${t.term_key}`} />
              )}
              <button type="button" onClick={() => setTerms((ts) => ts.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-red-500" aria-label={tf("Retirer")}><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-3">
        <div className="flex items-center gap-2">
          <label className="text-[11px] text-muted-foreground whitespace-nowrap" htmlFor="neg-valid-until">{tf("Valable jusqu'au")} ({tf("optionnel")})</label>
          <Input id="neg-valid-until" type="datetime-local" value={validUntil} onChange={(e) => setValidUntil(e.target.value)}
            className="h-9 w-52" data-testid="neg-draft-valid-until" />
        </div>
        <Select value={addKey} onValueChange={addTerm}>
          <SelectTrigger className="h-9 w-56" data-testid="neg-add-term-select"><SelectValue placeholder={tf("Ajouter un élément")} /></SelectTrigger>
          <SelectContent className="max-h-64">
            {(catalog || []).filter((c) => !terms.some((t) => t.term_key === c.term_key)).map((c) => <SelectItem key={c.term_key} value={c.term_key}>{tf(c.label)}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <Button variant="outline" size="sm" className="h-9" disabled={busy} onClick={() => run(save, tfr("Brouillon enregistré"))} data-testid="neg-draft-save">{tf("Enregistrer")}</Button>
        <Button size="sm" className="h-9" disabled={busy || terms.length === 0} data-testid="neg-draft-finalize"
          onClick={() => run(async () => { await save(); await api.post(`/negotiations/${neg.id}/versions/${draft.id}/finalize`); }, tfr("Version finalisée"))}>
          <Check className="h-3.5 w-3.5 mr-1.5" /> {tf("Finaliser")}
        </Button>
      </div>
    </div>
  );
}

function ReviewDialog({ open, onOpenChange, neg, checklist, act }) {
  const { tf } = useLang();
  const [checks, setChecks] = useState(neg.final_review || {});
  useEffect(() => setChecks(neg.final_review || {}), [neg.final_review]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="neg-review-dialog">
        <DialogTitle>{tf("Revue finale avant signature")}</DialogTitle>
        <DialogDescription>{tf("Vérifiez chaque point avant de passer le contrat en « Prêt à signer ».")}</DialogDescription>
        <div className="space-y-2.5 mt-2">
          {(checklist || []).map((k) => (
            <label key={k} className="flex items-center gap-2.5 text-sm cursor-pointer" data-testid={`neg-check-${k}`}>
              <Checkbox checked={!!checks[k]} onCheckedChange={(v) => setChecks((c) => ({ ...c, [k]: !!v }))} />
              {tf(CHECK_FR[k])}
            </label>
          ))}
        </div>
        <Button className="mt-3" data-testid="neg-review-save"
          onClick={() => { act(() => api.post(`/negotiations/${neg.id}/final-review`, { checklist: checks }), tfr("Revue finale enregistrée")); onOpenChange(false); }}>
          {tf("Enregistrer")}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export default TabNegotiation;
